/*
// For Assistant Name
<AIInputHelper
  title={t("Generate Assistant Name")}
  inputPlaceholder={t("Describe what this assistant is for...")}
  contextDescription={t("assistant name that is concise and descriptive")}
  onPromptGenerated={setName}
/>

// For Assistant Description
<AIInputHelper
  title={t("Generate Assistant Description")}
  inputPlaceholder={t("Describe how this assistant should behave...")}
  contextDescription={t("assistant description that explains its purpose and capabilities")}
  onPromptGenerated={setDescription}
/>

// For System Prompt
<AIInputHelper
  title={t("Generate System Prompt")}
  inputPlaceholder={t("Describe the assistant's role and behavior in detail...")}
  contextDescription={t("system prompt that defines the assistant's capabilities, personality, and constraints")}
  onPromptGenerated={setPrompt}
/>

// For Conversation Starters
<AIInputHelper
  title={t("Generate Conversation Starter")}
  inputPlaceholder={t("What kind of questions should users ask this assistant?")}
  contextDescription={t("engaging conversation starter that showcases the assistant's capabilities")}
  onPromptGenerated={addConversationStarter}
/>

// For Prompt Creation
<AIInputHelper
  title={t("Generate Prompt Template")}
  inputPlaceholder={t("What should this prompt help users achieve?")}
  contextDescription={t("reusable prompt template for specific use cases")}
  onPromptGenerated={setPromptTemplate}
/>

// For Tool Description
<AIInputHelper
  title={t("Generate Tool Description")}
  inputPlaceholder={t("What does this tool do and how should it be used?")}
  contextDescription={t("clear and technical description of the tool's functionality")}
  onPromptGenerated={setToolDescription}
/>
*/
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Image from 'next/image'
import logo from '@/public/losi-icon.png'
import { IconLoader2, IconSparkle2, IconX } from '@tabler/icons-react'
import { toast } from 'sonner'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { TextareaAutosize } from '@/components/ui/textarea-autosize'

interface AIInputHelperProps {
  onPromptGenerated: (prompt: string) => void
  contextDescription: string
  inputPlaceholder: string
  title?: string
  buttonClassName?: string
  popoverClassName?: string
  onOpenChange?: (isOpen: boolean) => void
  isHeader?: boolean
}

export const AIInputHelper: FC<AIInputHelperProps> = ({
  onPromptGenerated,
  contextDescription,
  inputPlaceholder,
  title,
  buttonClassName,
  popoverClassName,
  onOpenChange,
  isHeader = false
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [userInput, setUserInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen)
    }
  }, [isOpen, onOpenChange])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement | Element>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleGeneratePrompt()
    }
  }

  const handleGeneratePrompt = async () => {
    if (!userInput.trim()) {
      toast.error(t('Please enter a description'))
      return
    }

    setIsLoading(true)

    try {
      const response = await fetch('/api/generate-prompt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userInput,
          contextDescription: t(contextDescription)
        })
      })

      if (!response.ok) {
        throw new Error(t('Failed to generate prompt'))
      }

      const data = await response.json()
      onPromptGenerated(data.prompt)
      setUserInput('')
      setIsOpen(false)
    } catch (error) {
      console.error(error)
      toast.error(t('Failed to generate prompt'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative inline-block">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(isHeader ? 'h-26 w-26' : 'size-16', buttonClassName)}
        title={t('AI Generate')}
      >
        <IconSparkle2 size={isHeader ? 24 : 16} />
      </Button>

      {isOpen && (
        <>
          <div
            className="bg-background/80 fixed inset-0 z-40 backdrop-blur-sm sm:hidden"
            onClick={() => setIsOpen(false)}
          />

          <div
            className={cn(
              'bg-background fixed z-50 mt-2 rounded-lg border p-4 shadow-lg sm:absolute',
              'w-[90vw] max-w-[400px] sm:w-72',
              'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
              'sm:left-auto sm:right-0 sm:top-full sm:translate-x-0 sm:translate-y-0',
              popoverClassName
            )}
          >
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Image
                  src={logo.src}
                  alt={t('Losi')}
                  width={16}
                  height={16}
                  style={{ borderRadius: 5 }}
                />
                <span className="text-sm font-medium">{t(title || '')}</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="size-6"
                onClick={() => setIsOpen(false)}
              >
                <IconX size={14} />
              </Button>
            </div>

            <TextareaAutosize
              placeholder={t(inputPlaceholder)}
              value={userInput}
              onValueChange={setUserInput}
              onKeyDown={handleKeyDown}
              minRows={3}
              maxRows={6}
              className="border-input bg-background mb-2 w-full rounded-md border px-3 py-2"
            />

            <Button className="w-full gap-2" onClick={handleGeneratePrompt} disabled={isLoading}>
              {isLoading ? (
                <IconLoader2 className="size-4 animate-spin" />
              ) : (
                <IconSparkle2 className="size-4" />
              )}
              <span>{isLoading ? t('Generating...') : t('Generate')}</span>
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
